// Pure geometry for ImageCropperDialog.

// Must match .lesson-card__thumbnail's aspect-ratio in SubChapterCard.scss —
// the frame previews exactly what the card will show.
const FRAME_ASPECT = 4 / 3;
const FRAME_MARGIN = 40;

// Guaranteed zoom-in range even for small images; large images can zoom
// further, up to the point where the crop would render blurry on the card.
const BASE_MAX_ZOOM = 4;
const MIN_CROP_WIDTH = 430;

// Cap the exported image width; big uploads get downscaled so cards load fast.
const MAX_OUTPUT_WIDTH = 1600;

// The crop frame: the largest FRAME_ASPECT box that fits the stage with
// FRAME_MARGIN around it.
function computeFrame(stage) {
    let w = stage.w - FRAME_MARGIN * 2;
    let h = w / FRAME_ASPECT;
    const maxH = stage.h - FRAME_MARGIN * 2;
    if (h > maxH) {
        h = maxH;
        w = h * FRAME_ASPECT;
    }
    return {w, h};
}

// Scale from natural image pixels to on-screen pixels: at zoom 1 the image
// exactly covers the frame (like object-fit: cover on the lesson card).
function displayScale(imgSize, frame, zoom) {
    return Math.max(frame.w / imgSize.w, frame.h / imgSize.h) * zoom;
}

function clampOffset(offset, zoom, imgSize, frame) {
    const s = displayScale(imgSize, frame, zoom);
    const maxX = Math.max(0, (imgSize.w * s - frame.w) / 2);
    const maxY = Math.max(0, (imgSize.h * s - frame.h) / 2);
    return {
        x: Math.min(maxX, Math.max(-maxX, offset.x)),
        y: Math.min(maxY, Math.max(-maxY, offset.y)),
    };
}

// min: zooming out stops once the whole image fits inside the frame (contain).
// max: zooming in stops once the crop would be under MIN_CROP_WIDTH natural
// pixels wide (with BASE_MAX_ZOOM always available).
function zoomBounds(imgSize, frame) {
    const cover = Math.max(frame.w / imgSize.w, frame.h / imgSize.h);
    const contain = Math.min(frame.w / imgSize.w, frame.h / imgSize.h);
    return {
        min: Math.min(1, contain / cover),
        max: Math.max(BASE_MAX_ZOOM, frame.w / (cover * MIN_CROP_WIDTH)),
    };
}

// The framed area in natural image coordinates (the drawImage source rect).
// Coordinates may extend past the image when zoomed out below cover — the
// overhang becomes padding in the export.
function cropRect(imgSize, frame, zoom, offset) {
    const s = displayScale(imgSize, frame, zoom);
    const off = clampOffset(offset, zoom, imgSize, frame);
    const sw = frame.w / s;
    const sh = frame.h / s;
    return {
        sx: (imgSize.w - sw) / 2 - off.x / s,
        sy: (imgSize.h - sh) / 2 - off.y / s,
        sw,
        sh,
    };
}

function outputSize(sw, sh) {
    const w = Math.max(1, Math.round(Math.min(MAX_OUTPUT_WIDTH, sw)));
    return {w, h: Math.max(1, Math.round(w * sh / sw))};
}

module.exports = {
    FRAME_ASPECT,
    FRAME_MARGIN,
    BASE_MAX_ZOOM,
    MIN_CROP_WIDTH,
    MAX_OUTPUT_WIDTH,
    computeFrame,
    displayScale,
    clampOffset,
    zoomBounds,
    cropRect,
    outputSize,
};
